import { type ToolSpec, n, s, fmt, money } from "./types";

const CURRENCIES = [
  { value: "$", label: "USD $" },
  { value: "£", label: "GBP £" },
  { value: "€", label: "EUR €" },
  { value: "C$", label: "CAD C$" },
  { value: "A$", label: "AUD A$" },
];

export const FINANCE_TOOLS: ToolSpec[] = [
  {
    slug: "hourly-to-annual-salary-converter",
    cluster: "finance",
    name: "Hourly to Annual Salary Converter",
    h1: "Hourly to Annual Salary Converter (Overtime, PTO and Unpaid Weeks)",
    title: "Hourly to Annual Salary Converter with Overtime & PTO",
    description:
      "Convert an hourly wage into weekly, bi-weekly, monthly and annual pay including overtime multipliers, paid time off and unpaid leave weeks.",
    keywords: [
      "hourly to annual salary converter with overtime tax",
      "convert 25 dollars an hour to yearly salary",
      "hourly wage to unadjusted gross annual salary",
      "how much is 40 hours a week annually with 2 weeks paid vacation",
      "hourly to monthly salary breakdown after overtime",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: CURRENCIES },
      { id: "rate", label: "Hourly pay rate", type: "number", def: "25", unit: "per hour", min: 1, step: 0.25 },
      { id: "hours", label: "Regular hours per week", type: "number", def: "40", min: 1, max: 80 },
      { id: "ot", label: "Overtime hours per week", type: "number", def: "0", min: 0, max: 40 },
      { id: "otmult", label: "Overtime multiplier", type: "number", def: "1.5", min: 1, max: 3, step: 0.25 },
      { id: "pto", label: "Paid time off", type: "number", def: "2", unit: "weeks", min: 0, max: 12 },
      { id: "unpaid", label: "Unpaid time off", type: "number", def: "0", unit: "weeks", min: 0, max: 20 },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const rate = n(v, "rate"), hrs = n(v, "hours"), ot = n(v, "ot"), mult = n(v, "otmult", 1.5);
      const weeklyReg = rate * hrs;
      const weeklyOt = rate * mult * ot;
      const weekly = weeklyReg + weeklyOt;
      const paidWeeks = Math.max(0, 52 - n(v, "unpaid"));
      const unadjusted = weekly * 52;
      const adjusted = weekly * paidWeeks;
      return {
        cards: [
          { label: "Adjusted annual", value: money(adjusted, c), sub: `${paidWeeks} paid weeks`, accent: true },
          { label: "Unadjusted annual", value: money(unadjusted, c), sub: "Straight 52-week maths" },
          { label: "Monthly", value: money(adjusted / 12, c), sub: "Gross, before tax" },
          { label: "Bi-weekly", value: money(weekly * 2, c), sub: "26 pay periods a year" },
        ],
        table: {
          title: "Your full pay breakdown",
          head: ["Period", "Gross pay", "Notes"],
          rows: [
            ["Per hour", money(rate, c, 2), `Overtime paid at ${money(rate * mult, c, 2)}`],
            ["Per day (8 h)", money(rate * 8, c), "Regular rate only"],
            ["Per week", money(weekly, c), `${hrs} regular + ${ot} overtime hours`],
            ["Bi-weekly", money(weekly * 2, c), "Most common US pay cycle"],
            ["Per month", money(adjusted / 12, c), "Adjusted for unpaid leave"],
            ["Per year", money(adjusted, c), `${fmt((hrs + ot) * paidWeeks)} billable hours`],
          ],
        },
        summary: `Overtime adds ${money(weeklyOt * paidWeeks, c)} a year — ${fmt(unadjusted ? (weeklyOt / weekly) * 100 : 0, 1)}% of your gross.`,
      };
    },
    intro:
      "Converting an hourly wage to a salary looks like a single multiplication, but three variables quietly change the answer: how many hours you are actually scheduled, whether overtime is paid at a premium, and how many weeks of the year you are paid without working. This converter separates the unadjusted figure recruiters quote from the adjusted figure that hits your bank account.",
    sections: [
      { h: "The 2,080 hour standard year", body: "Forty hours across fifty-two weeks equals exactly 2,080 hours. This is the baseline every US salary comparison uses, which is why $25 an hour is quoted as $52,000 a year. A 37.5-hour UK week gives 1,950 hours, and a 30-hour part-time week gives 1,560 — enough to change an apparently identical hourly rate into a very different annual number." },
      { h: "Paid versus unpaid time off", body: "Paid leave does not reduce your annual income, because you are paid for those weeks without working. Unpaid leave does: two unpaid weeks cut billable hours from 2,080 to 2,000 and knock 3.85% off gross pay. The calculator only subtracts unpaid weeks, which is why the PTO field affects your effective hourly value rather than the total." },
      { h: "Overtime rules that change the maths", body: "US federal FLSA rules require 1.5× for hours above 40 in a week for non-exempt workers. Some states add daily overtime above 8 hours and double time above 12. UK law has no statutory premium — overtime pay is contractual. Set the multiplier to match your own agreement rather than assuming time-and-a-half." },
    ],
    staticTable: {
      title: "Hourly wage to annual salary reference",
      head: ["Hourly rate", "30 h week", "37.5 h week", "40 h week", "40 h + 5 h OT"],
      rows: [
        ["$15", "$23,400", "$29,250", "$31,200", "$37,050"],
        ["$20", "$31,200", "$39,000", "$41,600", "$49,400"],
        ["$25", "$39,000", "$48,750", "$52,000", "$61,750"],
        ["$30", "$46,800", "$58,500", "$62,400", "$74,100"],
        ["$40", "$62,400", "$78,000", "$83,200", "$98,800"],
        ["$50", "$78,000", "$97,500", "$104,000", "$123,500"],
        ["$75", "$117,000", "$146,250", "$156,000", "$185,250"],
        ["$100", "$156,000", "$195,000", "$208,000", "$247,000"],
      ],
    },
    faqs: [
      { q: "How many working hours are in a standard year?", a: "A full-time year at 40 hours per week across 52 weeks contains exactly 2,080 working hours. That is the divisor used to convert salaries back into hourly rates." },
      { q: "How do unpaid vacation weeks affect my annual salary?", a: "Unpaid leave reduces billable hours. Two unpaid weeks cut a 2,080-hour year to 2,000 hours, lowering gross annual pay by about 3.8% at the same hourly rate." },
      { q: "Is $25 an hour a good salary?", a: "At 40 hours a week it is $52,000 gross before tax — around the US median individual income, though purchasing power varies enormously by state and city." },
    ],
    related: ["freelance-hourly-rate-calculator", "side-hustle-profit-calculator", "fire-calculator-retire-early", "compound-interest-visualizer"],
  },

  {
    slug: "fire-calculator-retire-early",
    cluster: "finance",
    name: "FIRE (Retire Early) Calculator",
    h1: "Financial Independence Retire Early (FIRE) Calculator with Inflation Adjustment",
    title: "FIRE Calculator — Retire Early Corpus & Age Projection",
    description:
      "Project your FIRE number, retirement age and Coast FIRE benchmark using your savings rate, expected return and safe withdrawal rate. Lean, Standard and Fat FIRE modes.",
    keywords: [
      "financial independence retire early fire calculator",
      "coast fire vs lean fire vs fat fire calculator",
      "fire target corpus calculator 4 percent safe withdrawal rate",
      "how much money do i need to retire early",
      "fire milestone calculator adjusted for inflation",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: CURRENCIES },
      { id: "age", label: "Current age", type: "number", def: "32", unit: "years", min: 16, max: 70 },
      { id: "expenses", label: "Annual expenses", type: "number", def: "45000", min: 1000, step: 1000 },
      { id: "networth", label: "Current invested net worth", type: "number", def: "120000", min: 0, step: 1000 },
      { id: "monthly", label: "Monthly savings", type: "number", def: "2200", min: 0, step: 50 },
      { id: "ret", label: "Expected real return", type: "number", def: "7", unit: "% / yr", min: 0, max: 15, step: 0.5 },
      { id: "swr", label: "Safe withdrawal rate", type: "number", def: "4", unit: "%", min: 2, max: 8, step: 0.1 },
      { id: "type", label: "FIRE type", type: "select", def: "1", options: [
        { value: "0.75", label: "Lean FIRE — 75% of expenses" },
        { value: "1", label: "Standard FIRE — 100%" },
        { value: "1.25", label: "Fat FIRE — 125%" },
        { value: "1.5", label: "Fat FIRE+ — 150%" },
      ] },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const exp = n(v, "expenses") * n(v, "type", 1);
      const swr = n(v, "swr", 4) / 100;
      const corpus = swr > 0 ? exp / swr : 0;
      const r = n(v, "ret", 7) / 100 / 12;
      let bal = n(v, "networth");
      const contrib = n(v, "monthly");
      let months = 0;
      while (bal < corpus && months < 720) { bal = bal * (1 + r) + contrib; months++; }
      const age = n(v, "age");
      const fireAge = age + months / 12;
      const yearsToTrad = Math.max(0, 65 - age);
      const coast = corpus / Math.pow(1 + n(v, "ret", 7) / 100, yearsToTrad);
      const savingsRate = (contrib * 12) / Math.max(1, contrib * 12 + n(v, "expenses")) * 100;
      const rows: (string | number)[][] = [];
      let b = n(v, "networth");
      for (let y = 1; y <= 10; y++) {
        for (let m = 0; m < 12; m++) b = b * (1 + r) + contrib;
        rows.push([`Age ${age + y}`, money(b, c), money(corpus, c), `${fmt(Math.min(100, (b / Math.max(corpus, 1)) * 100))}%`]);
      }
      return {
        cards: [
          { label: "FIRE number", value: money(corpus, c), sub: `${fmt(1 / swr, 1)}× annual expenses`, accent: true },
          { label: "FIRE age", value: months >= 720 ? "60+ yrs away" : `${fmt(fireAge, 1)}`, sub: months >= 720 ? "Increase savings rate" : `${fmt(months / 12, 1)} years from now` },
          { label: "Coast FIRE today", value: money(coast, c), sub: `Enough to coast to 65 with zero new savings` },
          { label: "Savings rate", value: `${fmt(savingsRate, 1)}%`, sub: "Of savings + spending" },
        ],
        table: { title: "Ten-year compound growth path", head: ["Year", "Projected portfolio", "FIRE target", "Progress"], rows },
        summary: `At a ${n(v, "swr", 4)}% withdrawal rate you can safely draw ${money(corpus * swr, c)} a year from ${money(corpus, c)}.`,
      };
    },
    intro:
      "FIRE arithmetic is deceptively simple: your target corpus is annual spending divided by your safe withdrawal rate, so a 4% rate means 25 times expenses. What actually determines the date is your savings rate — the share of take-home pay you invest — because it simultaneously raises contributions and lowers the number you need.",
    sections: [
      { h: "The 4% rule and where it came from", body: "The Trinity Study tested historical US portfolios of stocks and bonds over rolling 30-year windows and found that withdrawing 4% of the initial balance, inflation-adjusted, survived almost every period. Longer retirements of 40-50 years, which early retirees face, are usually modelled at 3.25-3.5% instead." },
      { h: "Coast FIRE explained", body: "Coast FIRE is the balance at which compound growth alone reaches your target by traditional retirement age. Once you hit it you can stop contributing entirely and simply cover living costs with any job. The card above discounts your FIRE number back to today at your expected return over the years remaining until 65." },
      { h: "Sequence of returns risk", body: "Two retirees with identical average returns can end up in completely different places if one meets a bear market in the first three years. Mitigations include a two-year cash buffer, a rising equity glidepath, and flexible spending rules that trim withdrawals by 10% after a down year." },
    ],
    staticTable: {
      title: "The four types of FIRE",
      head: ["Type", "Expense multiplier", "Typical corpus (on $45k spend)", "Withdrawal margin", "Best suited to"],
      rows: [
        ["Lean FIRE", "0.75×", "$843,750", "Tight — needs flexibility", "Low cost-of-living, minimalists"],
        ["Barista FIRE", "0.6× + part-time income", "$675,000", "Wide — wages cover gaps", "Those wanting health cover"],
        ["Coast FIRE", "Front-loaded", "Varies by age", "N/A until traditional age", "Young high earners"],
        ["Standard FIRE", "1.0×", "$1,125,000", "Trinity-tested 4%", "Most FIRE followers"],
        ["Fat FIRE", "1.25-1.5×", "$1.4m-$1.7m", "Very wide", "High earners, families"],
      ],
    },
    faqs: [
      { q: "What is the 4% safe withdrawal rate rule?", a: "Derived from the Trinity Study, it states that withdrawing 4% of your initial portfolio annually, adjusted for inflation, had a very low probability of depletion over a 30-year retirement. Early retirees often use 3.25-3.5% for longer horizons." },
      { q: "How does Coast FIRE differ from traditional FIRE?", a: "Coast FIRE means you have already invested enough that compound growth alone will reach your retirement target by traditional retirement age, even if you never contribute another dollar." },
      { q: "Does the FIRE number account for inflation?", a: "Yes, when you enter a real (post-inflation) return such as 7% rather than a nominal 10%. All balances then represent today's purchasing power." },
    ],
    related: ["compound-interest-visualizer", "mortgage-extra-payment-calculator", "inflation-purchasing-power-calculator", "debt-snowball-vs-avalanche-calculator"],
  },

  {
    slug: "compound-interest-visualizer",
    cluster: "finance",
    name: "Compound Interest Visualizer",
    h1: "Compound Interest Calculator with Monthly Contributions (Visual Growth Curve)",
    title: "Compound Interest Calculator with Monthly Contributions",
    description:
      "See principal, contributions and compound interest split year by year. Supports monthly deposits, any compounding frequency and a full growth table.",
    keywords: [
      "compound interest calculator monthly contribution visual",
      "how much will 500 a month grow in 20 years",
      "daily vs monthly vs annual compounding difference",
      "interest earned vs contributions chart",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: CURRENCIES },
      { id: "principal", label: "Starting principal", type: "number", def: "10000", min: 0, step: 500 },
      { id: "monthly", label: "Monthly deposit", type: "number", def: "500", min: 0, step: 50 },
      { id: "rate", label: "Annual interest rate", type: "number", def: "8", unit: "%", min: 0, max: 30, step: 0.25 },
      { id: "years", label: "Years invested", type: "number", def: "20", min: 1, max: 60 },
      { id: "freq", label: "Compounding frequency", type: "select", def: "12", options: [
        { value: "1", label: "Annually" },
        { value: "4", label: "Quarterly" },
        { value: "12", label: "Monthly" },
        { value: "365", label: "Daily" },
      ] },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const p = n(v, "principal"), m = n(v, "monthly"), rate = n(v, "rate") / 100, yrs = n(v, "years", 1), f = n(v, "freq", 12);
      const perPeriod = rate / f;
      const periods = f * yrs;
      const depositPerPeriod = (m * 12) / f;
      let bal = p;
      const rows: (string | number)[][] = [];
      for (let i = 1; i <= periods; i++) {
        bal = bal * (1 + perPeriod) + depositPerPeriod;
        if (i % f === 0) {
          const y = i / f;
          const contributed = p + m * 12 * y;
          if (y <= 10 || y % 5 === 0) rows.push([`Year ${y}`, money(bal, c), money(contributed, c), money(bal - contributed, c)]);
        }
      }
      const contributedTotal = p + m * 12 * yrs;
      return {
        cards: [
          { label: "Final balance", value: money(bal, c), sub: `${yrs} years at ${n(v, "rate")}%`, accent: true },
          { label: "You contributed", value: money(contributedTotal, c), sub: `${fmt((contributedTotal / Math.max(bal, 1)) * 100)}% of the total` },
          { label: "Interest earned", value: money(bal - contributedTotal, c), sub: `${fmt(((bal - contributedTotal) / Math.max(bal, 1)) * 100)}% of the total` },
          { label: "Growth multiple", value: `${fmt(bal / Math.max(contributedTotal, 1), 2)}×`, sub: "Balance ÷ money in" },
        ],
        bars: [
          { label: "Contributions", value: contributedTotal, display: money(contributedTotal, c) },
          { label: "Interest", value: bal - contributedTotal, display: money(bal - contributedTotal, c) },
        ],
        table: { title: "Year-by-year growth", head: ["Year", "Balance", "Contributed", "Interest earned"], rows },
      };
    },
    intro:
      "Compound interest is interest earned on interest already earned. The mathematics are unremarkable — A = P(1 + r/n)^(nt) — but the behaviour is not: with regular monthly deposits the interest component overtakes contributions somewhere between years 14 and 20 at typical equity returns, and everything after that point is exponential rather than linear.",
    sections: [
      { h: "Why the crossover year matters more than the rate", body: "Most people compare interest rates. What actually decides outcomes is how long you stay invested past the crossover point where cumulative interest exceeds cumulative deposits. At 8% with $500 a month, that crossover lands around year 16 — and the ten years after it produce more growth than the sixteen before it." },
      { h: "Compounding frequency is a rounding error", body: "Moving from annual to daily compounding at 8% raises the effective annual rate from 8.00% to 8.33%. Real. Measurable. But trivial next to adding two years to the horizon or $100 to the monthly deposit. Do not shop for compounding frequency; shop for fees and consistency." },
      { h: "Nominal returns versus real returns", body: "An 8% nominal return with 3% inflation is a 4.85% real return, not 5%: real return equals (1 + nominal) ÷ (1 + inflation) − 1. If you want the balance to represent today's purchasing power, enter the real rate here and read every future figure in today's money." },
    ],
    staticTable: {
      title: "What $500 a month becomes",
      head: ["Years", "At 4%", "At 6%", "At 8%", "At 10%", "Total deposited"],
      rows: [
        [5, "$33,180", "$34,885", "$36,707", "$38,653", "$30,000"],
        [10, "$73,625", "$81,940", "$91,473", "$102,422", "$60,000"],
        [15, "$123,908", "$145,409", "$173,072", "$207,235", "$90,000"],
        [20, "$183,349", "$231,020", "$294,510", "$379,684", "$120,000"],
        [25, "$257,384", "$346,497", "$475,513", "$663,594", "$150,000"],
        [30, "$347,024", "$502,257", "$745,180", "$1,130,244", "$180,000"],
      ],
    },
    faqs: [
      { q: "How much will $500 a month grow to in 20 years?", a: "At an 8% annual return with monthly compounding, roughly $294,500 — of which $120,000 is your deposits and about $174,500 is compound growth." },
      { q: "Does compounding frequency make a big difference?", a: "Marginally. At 8%, daily compounding beats annual compounding by about 0.33 percentage points of effective yield. Time in the market matters far more." },
      { q: "Should I use nominal or real returns?", a: "Use real (inflation-adjusted) returns if you want the answer in today's purchasing power. Around 7% real is the long-run historical figure for a globally diversified equity portfolio." },
    ],
    related: ["fire-calculator-retire-early", "inflation-purchasing-power-calculator", "mortgage-extra-payment-calculator", "hourly-to-annual-salary-converter"],
  },

  {
    slug: "freelance-hourly-rate-calculator",
    cluster: "finance",
    name: "Freelancer Billable Rate",
    h1: "Freelance Hourly Rate Calculator (From Desired Annual Salary)",
    title: "Freelance Hourly Rate Calculator from Target Salary",
    description:
      "Work out the hourly rate you must charge as a freelancer once non-billable time, business expenses, self-employment tax and unpaid holidays are covered.",
    keywords: [
      "freelance hourly rate calculator desired annual salary",
      "how to price freelance work per hour",
      "billable utilisation rate freelancer formula",
      "day rate vs hourly rate contractor calculator",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: CURRENCIES },
      { id: "salary", label: "Target take-home income", type: "number", def: "80000", min: 5000, step: 1000 },
      { id: "expenses", label: "Annual business expenses", type: "number", def: "9000", min: 0, step: 500 },
      { id: "tax", label: "Effective tax + insurance", type: "number", def: "28", unit: "%", min: 0, max: 60 },
      { id: "weeks", label: "Working weeks per year", type: "number", def: "46", min: 20, max: 52 },
      { id: "hours", label: "Hours worked per week", type: "number", def: "40", min: 5, max: 80 },
      { id: "util", label: "Billable utilisation", type: "number", def: "65", unit: "%", min: 20, max: 100, help: "Share of hours a client actually pays for." },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const gross = (n(v, "salary") / (1 - n(v, "tax") / 100)) + n(v, "expenses");
      const billable = n(v, "weeks") * n(v, "hours") * (n(v, "util", 65) / 100);
      const rate = billable > 0 ? gross / billable : 0;
      return {
        cards: [
          { label: "Minimum hourly rate", value: money(rate, c, 2), sub: `${fmt(billable)} billable hours a year`, accent: true },
          { label: "Recommended day rate", value: money(rate * 8, c), sub: "8 billable hours" },
          { label: "Revenue needed", value: money(gross, c), sub: "Before tax and expenses" },
          { label: "Weekly billable target", value: `${fmt((n(v, "hours") * n(v, "util", 65)) / 100, 1)} h`, sub: `From a ${n(v, "hours")} hour week` },
        ],
        table: {
          title: "Rate sensitivity by utilisation",
          head: ["Utilisation", "Billable hours / yr", "Required hourly rate", "Equivalent day rate"],
          rows: [40, 50, 60, 70, 80, 90].map((u) => {
            const b = n(v, "weeks") * n(v, "hours") * (u / 100);
            return [`${u}%`, fmt(b), money(gross / b, c, 2), money((gross / b) * 8, c)];
          }),
        },
        summary: `Every 10 points of lost utilisation forces roughly a ${fmt(100 / (n(v, "util", 65) - 10) * 10, 0)}% rate increase to stand still.`,
      };
    },
    intro:
      "Freelancers routinely price by comparing their target salary to an employee's hourly equivalent, which understates the rate by 60-100%. An employee bills nobody for admin, sales calls, sick days, holidays or software; a freelancer pays for all of it out of the same rate, and pays self-employment tax on top.",
    sections: [
      { h: "Utilisation is the hidden variable", body: "Most independent professionals bill 55-70% of their working hours. The remainder goes to pitching, invoicing, contracts, learning and unpaid revisions. Dropping from 70% to 55% utilisation raises the required rate by 27% for the same take-home pay, which is why marketing efficiency is a pricing lever, not a side task." },
      { h: "Costs employees never see", body: "Health insurance, pension contributions, equipment, accounting, professional liability cover, software subscriptions and co-working space all sit inside your rate. Budget them as an annual expense line rather than absorbing them silently — the expenses field feeds directly into the required revenue figure." },
      { h: "Moving from hourly to value pricing", body: "Hourly billing caps your income at your available hours and penalises you for getting faster. Once you know your floor rate, use it as a sanity check on fixed-price quotes: divide the quoted fee by realistic delivery hours and confirm the implied rate clears the floor." },
    ],
    staticTable: {
      title: "Employee salary to freelance rate equivalence",
      head: ["Employee salary", "Naive hourly (÷2080)", "Realistic freelance rate (65% util)", "Uplift"],
      rows: [
        ["$50,000", "$24", "$52", "2.2×"],
        ["$70,000", "$34", "$72", "2.1×"],
        ["$90,000", "$43", "$93", "2.2×"],
        ["$120,000", "$58", "$124", "2.1×"],
        ["$150,000", "$72", "$155", "2.2×"],
      ],
      caption: "Assumes 28% tax and insurance load plus $9,000 of business expenses.",
    },
    faqs: [
      { q: "How do I calculate my freelance hourly rate?", a: "Gross up your target take-home for tax, add annual business expenses, then divide by the hours clients will actually pay for — working weeks × weekly hours × utilisation rate." },
      { q: "What utilisation rate is realistic?", a: "Established freelancers average 60-70%. Newer freelancers spending heavy time on business development often sit at 40-50%, which materially raises the rate they must charge." },
      { q: "Should I quote a day rate instead?", a: "Day rates simplify negotiation and discourage clients from micro-counting hours. Set it at eight billable hours of your calculated rate, and define what a day covers in the contract." },
    ],
    related: ["side-hustle-profit-calculator", "hourly-to-annual-salary-converter", "fire-calculator-retire-early", "tip-and-bill-splitter"],
  },

  {
    slug: "tip-and-bill-splitter",
    cluster: "finance",
    name: "Tip & Equal Bill Splitter",
    h1: "Restaurant Tip Calculator and Bill Splitter (Per Person, Evenly)",
    title: "Tip Calculator & Bill Splitter — Per Person Totals",
    description:
      "Split a restaurant bill evenly, add any tip percentage, handle pre-tax tipping and rounding, and see exactly what each person owes.",
    keywords: [
      "restaurant tip calculator split bill per person evenly",
      "how much to tip on 100 dollar bill",
      "tip on pre tax or post tax amount",
      "split bill calculator with tax and tip",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: CURRENCIES },
      { id: "bill", label: "Bill amount", type: "number", def: "120", min: 0, step: 0.5 },
      { id: "taxpct", label: "Sales tax already on the bill", type: "number", def: "8", unit: "%", min: 0, max: 30, step: 0.5 },
      { id: "tip", label: "Tip percentage", type: "number", def: "18", unit: "%", min: 0, max: 40 },
      { id: "basis", label: "Tip calculated on", type: "select", def: "pre", options: [
        { value: "pre", label: "Pre-tax subtotal (US convention)" },
        { value: "post", label: "Full bill including tax" },
      ] },
      { id: "people", label: "Number of people", type: "number", def: "4", min: 1, max: 40 },
      { id: "round", label: "Rounding", type: "select", def: "0", options: [
        { value: "0", label: "Exact to the cent" },
        { value: "1", label: "Round up to whole unit" },
        { value: "5", label: "Round up to nearest 5" },
      ] },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const bill = n(v, "bill"), taxPct = n(v, "taxpct");
      const subtotal = bill / (1 + taxPct / 100);
      const base = s(v, "basis") === "pre" ? subtotal : bill;
      const tip = (base * n(v, "tip")) / 100;
      const total = bill + tip;
      const people = Math.max(1, n(v, "people", 1));
      let each = total / people;
      const rd = n(v, "round");
      if (rd === 1) each = Math.ceil(each);
      if (rd === 5) each = Math.ceil(each / 5) * 5;
      return {
        cards: [
          { label: "Each person pays", value: money(each, c, 2), sub: `${people} way split`, accent: true },
          { label: "Total with tip", value: money(total, c, 2), sub: rd > 0 ? `Collected: ${money(each * people, c, 2)}` : "Exact" },
          { label: "Tip amount", value: money(tip, c, 2), sub: `${n(v, "tip")}% on the ${s(v, "basis") === "pre" ? "pre-tax subtotal" : "full bill"}` },
          { label: "Pre-tax subtotal", value: money(subtotal, c, 2), sub: `Tax portion ${money(bill - subtotal, c, 2)}` },
        ],
        table: {
          title: "Tip comparison at this bill",
          head: ["Tip %", "Tip amount", "Bill total", "Per person"],
          rows: [10, 15, 18, 20, 22, 25].map((p) => {
            const t = (base * p) / 100;
            return [`${p}%`, money(t, c, 2), money(bill + t, c, 2), money((bill + t) / people, c, 2)];
          }),
        },
      };
    },
    intro:
      "Tipping etiquette varies more than most travellers expect, and so does the base the tip is calculated on. In the United States the convention is a percentage of the pre-tax subtotal; in Canada many people tip on the total including GST/PST; in the UK a discretionary service charge is often already printed on the bill. This calculator lets you choose the base explicitly instead of assuming one.",
    sections: [
      { h: "Pre-tax versus post-tax tipping", body: "On a $120 bill carrying 8% sales tax, tipping 18% pre-tax gives $20.00; tipping the same percentage on the full amount gives $21.60. It is a $1.60 difference on one meal, but roughly $150 a year for someone eating out twice a week — worth being deliberate about." },
      { h: "Splitting when the bill is uneven", body: "Even splits are fastest but quietly overcharge the person who ordered a salad and tap water. A fair compromise used by many groups is to split shared items and tax evenly while assigning individual mains directly, then apply one group tip percentage across the whole total." },
      { h: "Rounding up without awkwardness", body: "Rounding each share up to the nearest whole unit collects slightly more than the bill and avoids fumbling for coins. The tool shows the total collected next to the true total so you can see the surplus and either add it to the tip or hand it back." },
    ],
    staticTable: {
      title: "International tipping norms",
      head: ["Country", "Restaurant norm", "Service charge common?", "Tip base"],
      rows: [
        ["United States", "18-22%", "Only for large parties", "Pre-tax subtotal"],
        ["Canada", "15-20%", "Rare", "Often post-tax"],
        ["United Kingdom", "10-12.5%", "Frequently added", "Total bill"],
        ["Australia", "0-10%", "No", "Total bill"],
        ["Germany", "5-10% rounded up", "No", "Total bill"],
        ["Japan", "None expected", "No", "—"],
      ],
    },
    faqs: [
      { q: "How much should I tip on a $100 bill?", a: "At the common US standard of 18-20% on the pre-tax subtotal, that is $18-$20 for a $100 pre-tax meal. Exceptional service is usually acknowledged at 22-25%." },
      { q: "Should you tip on tax?", a: "In the United States the customary base is the pre-tax subtotal, because sales tax is not part of the service value. Tipping on the total is generous but not expected." },
      { q: "How do you split a bill fairly when orders differ?", a: "Assign individually ordered items to each person, split shared plates and tax proportionally, then apply one tip percentage across everyone's subtotal." },
    ],
    related: ["rent-splitter-by-square-feet", "freelance-hourly-rate-calculator", "ev-fuel-trip-cost-splitter", "recipe-scale-converter"],
  },

  {
    slug: "rent-splitter-by-square-feet",
    cluster: "finance",
    name: "Sublease Rent Splitter",
    h1: "Sublease Room Rent Splitter Calculator (By Square Feet and Amenities)",
    title: "Room Rent Splitter Calculator by Square Feet",
    description:
      "Split shared rent fairly using room area, private bathroom premium and shared space allocation. Produces a per-room monthly figure both housemates can accept.",
    keywords: [
      "sublease room rent splitter calculator by square feet",
      "how to split rent fairly by room size",
      "rent split calculator private bathroom premium",
      "roommate rent proportional to bedroom area",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: CURRENCIES },
      { id: "rent", label: "Total monthly rent", type: "number", def: "2400", min: 100, step: 50 },
      { id: "shared", label: "Shared space weighting", type: "number", def: "40", unit: "%", min: 0, max: 80, help: "Share of rent divided equally regardless of room size." },
      { id: "a", label: "Room A area", type: "number", def: "180", unit: "sq ft", min: 20 },
      { id: "abath", label: "Room A bathroom", type: "select", def: "1", options: [{ value: "1", label: "Shared" }, { value: "1.12", label: "Private (+12%)" }] },
      { id: "b", label: "Room B area", type: "number", def: "130", unit: "sq ft", min: 20 },
      { id: "bbath", label: "Room B bathroom", type: "select", def: "1", options: [{ value: "1", label: "Shared" }, { value: "1.12", label: "Private (+12%)" }] },
      { id: "cRoom", label: "Room C area (0 if none)", type: "number", def: "0", unit: "sq ft", min: 0 },
    ],
    compute: (v) => {
      const cur = s(v, "cur", "$");
      const rent = n(v, "rent");
      const sharedPct = n(v, "shared") / 100;
      const rooms = [
        { name: "Room A", area: n(v, "a"), mult: n(v, "abath", 1) },
        { name: "Room B", area: n(v, "b"), mult: n(v, "bbath", 1) },
        { name: "Room C", area: n(v, "cRoom"), mult: 1 },
      ].filter((r) => r.area > 0);
      const count = rooms.length || 1;
      const sharedEach = (rent * sharedPct) / count;
      const privatePool = rent * (1 - sharedPct);
      const weighted = rooms.reduce((t, r) => t + r.area * r.mult, 0) || 1;
      const results = rooms.map((r) => ({ ...r, pay: sharedEach + (privatePool * r.area * r.mult) / weighted }));
      return {
        cards: results.slice(0, 4).map((r, i) => ({
          label: r.name,
          value: money(r.pay, cur),
          sub: `${fmt(r.area)} sq ft · ${fmt((r.pay / rent) * 100, 1)}% of rent`,
          accent: i === 0,
        })),
        table: {
          title: "Fair split breakdown",
          head: ["Room", "Area", "Equal share", "Area share", "Monthly total", "Per sq ft"],
          rows: results.map((r) => [r.name, `${fmt(r.area)} sq ft`, money(sharedEach, cur), money(r.pay - sharedEach, cur), money(r.pay, cur), money(r.pay / r.area, cur, 2)]),
        },
        summary: `${fmt(sharedPct * 100)}% of the rent is split equally for kitchen, living room and utilities; the rest follows room area.`,
      };
    },
    intro:
      "Splitting rent strictly by headcount punishes whoever takes the box room; splitting strictly by floor area ignores that everyone uses the kitchen equally. The standard fair-division approach used by housing co-ops is a hybrid: divide a fixed share of the rent equally for common space, then allocate the remainder in proportion to private area, adjusted for amenities like an en-suite.",
    sections: [
      { h: "Choosing the shared-space weighting", body: "In a flat with a large living room and one bathroom, 45-55% of rent reasonably belongs to common space. In a house of large bedrooms with a tiny galley kitchen, 25-35% is fairer. Agree this number before measuring rooms — it is the single biggest driver of the final split and much easier to negotiate in the abstract." },
      { h: "Pricing an en-suite or a balcony", body: "Market data on studio versus one-bed listings suggests a private bathroom is worth roughly 10-15% of a room's rent, a private balcony 5-8%, and a built-in wardrobe 2-4%. The multiplier in this calculator applies the bathroom premium to that room's weighted area rather than as a flat fee, which keeps small rooms from being penalised twice." },
      { h: "Handling sublets and mid-term moves", body: "When one housemate sublets for two months, calculate their monthly share here, then prorate by days occupied. Put the per-day figure in the sublease agreement — vague 'a third of rent' clauses are the most common source of housemate disputes when bills arrive at different times." },
    ],
    staticTable: {
      title: "Split methods compared on a $2,400 flat",
      head: ["Method", "180 sq ft room", "130 sq ft room", "Fairness note"],
      rows: [
        ["Equal headcount", "$1,200", "$1,200", "Ignores room size entirely"],
        ["Pure area", "$1,394", "$1,006", "Ignores shared space use"],
        ["Hybrid 40% shared", "$1,316", "$1,084", "Balanced default"],
        ["Hybrid 40% + en-suite A", "$1,373", "$1,027", "Amenity premium applied"],
        ["Hybrid 60% shared", "$1,238", "$1,162", "Best when common space is large"],
      ],
    },
    faqs: [
      { q: "How should roommates split rent fairly?", a: "Divide a set percentage of rent equally to cover shared rooms and utilities, then allocate the remainder in proportion to each private bedroom's area, with a premium for an en-suite bathroom." },
      { q: "How much extra should a private bathroom cost?", a: "Ten to fifteen percent of that room's rent is the market-consistent premium in most cities. Applying it as a multiplier on area keeps the adjustment proportional." },
      { q: "Should utilities be split by room size?", a: "No. Heating, internet and water track occupancy far more than area, so they are usually split equally per person and kept outside the rent formula." },
    ],
    related: ["tip-and-bill-splitter", "appliance-electricity-cost-calculator", "hourly-to-annual-salary-converter", "mortgage-extra-payment-calculator"],
  },

  {
    slug: "debt-snowball-vs-avalanche-calculator",
    cluster: "finance",
    name: "Debt Snowball vs Avalanche",
    h1: "Debt Snowball vs Avalanche Payoff Calculator (Timeline and Interest Saved)",
    title: "Debt Snowball vs Avalanche Calculator — Payoff Timeline",
    description:
      "Compare the snowball and avalanche debt payoff methods across up to four balances. See months to debt-free, total interest paid and which method wins for you.",
    keywords: [
      "debt snowball vs avalanche payoff calculator timeline",
      "which debt should i pay off first calculator",
      "credit card payoff calculator extra payment",
      "total interest saved avalanche method",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: CURRENCIES },
      { id: "b1", label: "Debt 1 balance", type: "number", def: "4200", min: 0, step: 100 },
      { id: "r1", label: "Debt 1 APR", type: "number", def: "24.9", unit: "%", min: 0, max: 60, step: 0.1 },
      { id: "b2", label: "Debt 2 balance", type: "number", def: "9800", min: 0, step: 100 },
      { id: "r2", label: "Debt 2 APR", type: "number", def: "12.5", unit: "%", min: 0, max: 60, step: 0.1 },
      { id: "b3", label: "Debt 3 balance", type: "number", def: "1500", min: 0, step: 100 },
      { id: "r3", label: "Debt 3 APR", type: "number", def: "18", unit: "%", min: 0, max: 60, step: 0.1 },
      { id: "budget", label: "Total monthly payment", type: "number", def: "700", min: 50, step: 25 },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const mk = () => [
        { bal: n(v, "b1"), apr: n(v, "r1") },
        { bal: n(v, "b2"), apr: n(v, "r2") },
        { bal: n(v, "b3"), apr: n(v, "r3") },
      ].filter((d) => d.bal > 0);
      const run = (order: "snowball" | "avalanche") => {
        const debts = mk().sort((a, b) => (order === "snowball" ? a.bal - b.bal : b.apr - a.apr));
        let months = 0, interest = 0;
        const budget = n(v, "budget");
        while (debts.some((d) => d.bal > 0.01) && months < 600) {
          let pool = budget;
          for (const d of debts) { if (d.bal > 0) { const i = (d.bal * d.apr) / 100 / 12; d.bal += i; interest += i; } }
          for (const d of debts) {
            if (d.bal <= 0) continue;
            const min = Math.min(d.bal, Math.max(25, d.bal * 0.02));
            const pay = Math.min(pool, min);
            d.bal -= pay; pool -= pay;
            if (pool <= 0) break;
          }
          for (const d of debts) {
            if (pool <= 0) break;
            if (d.bal <= 0) continue;
            const pay = Math.min(pool, d.bal);
            d.bal -= pay; pool -= pay;
          }
          months++;
        }
        return { months, interest };
      };
      const sn = run("snowball"), av = run("avalanche");
      const totalBal = mk().reduce((t, d) => t + d.bal, 0);
      return {
        cards: [
          { label: "Avalanche payoff", value: `${av.months} months`, sub: `Interest paid ${money(av.interest, c)}`, accent: true },
          { label: "Snowball payoff", value: `${sn.months} months`, sub: `Interest paid ${money(sn.interest, c)}` },
          { label: "Avalanche saves", value: money(Math.max(0, sn.interest - av.interest), c), sub: `${Math.max(0, sn.months - av.months)} months sooner` },
          { label: "Total debt", value: money(totalBal, c), sub: `${money(n(v, "budget"), c)} monthly budget` },
        ],
        table: {
          title: "Method comparison",
          head: ["Method", "Order of attack", "Months to debt-free", "Total interest", "Psychological pull"],
          rows: [
            ["Avalanche", "Highest APR first", av.months, money(av.interest, c), "Slower first win"],
            ["Snowball", "Smallest balance first", sn.months, money(sn.interest, c), "Fast first win"],
          ],
        },
        summary: sn.interest - av.interest < 200
          ? "The two methods are within a rounding error here — pick snowball for the motivation."
          : `Avalanche is mathematically ahead by ${money(sn.interest - av.interest, c)} of interest.`,
      };
    },
    intro:
      "Both payoff strategies pay the minimum on everything and throw every spare dollar at one target debt. They differ only in which target you choose: avalanche picks the highest interest rate, snowball picks the smallest balance. Avalanche always wins on arithmetic; snowball frequently wins in practice because a closed account in month two keeps people in the plan.",
    sections: [
      { h: "When the gap is worth caring about", body: "If your balances carry similar rates, the two methods finish within a month or two of each other and the interest difference is trivial — take the motivational route. The gap widens sharply when one large balance carries a much higher APR than a small one, which is the classic credit-card-plus-car-loan situation." },
      { h: "Minimum payments are a trap", body: "Card minimums are typically 2% of the balance or a fixed floor. Because that percentage shrinks as the balance falls, paying only minimums on a 24.9% APR card can take over 20 years and cost more in interest than the original purchase. Any fixed extra amount above the minimum dramatically shortens the tail." },
      { h: "Consider a balance transfer first", body: "Before optimising the order, check whether a 0% balance transfer or a fixed-rate consolidation loan is available. Moving a 24.9% balance to 0% for 18 months usually beats any reordering strategy — just budget for the 3-4% transfer fee and have a plan for the revert rate." },
    ],
    staticTable: {
      title: "Cost of minimum payments only (2% minimum)",
      head: ["Balance", "APR", "Years to clear", "Total interest", "With +$100/month"],
      rows: [
        ["$2,000", "19.9%", "16 yrs", "$2,780", "1 yr 7 mo"],
        ["$5,000", "22.9%", "24 yrs", "$9,450", "3 yr 6 mo"],
        ["$10,000", "24.9%", "31 yrs", "$22,600", "6 yr 4 mo"],
        ["$15,000", "18.9%", "28 yrs", "$22,100", "7 yr 9 mo"],
      ],
    },
    faqs: [
      { q: "Is the debt snowball or avalanche method better?", a: "Avalanche always costs less interest because it attacks the highest rate first. Snowball clears individual accounts faster, which studies of real repayment behaviour show improves the odds of finishing the plan." },
      { q: "Which debt should I pay off first?", a: "Mathematically, the one with the highest APR. Behaviourally, the smallest balance. If the interest difference between the two approaches is under a couple of hundred, take the smallest balance." },
      { q: "Do extra payments go to principal?", a: "On most revolving credit and instalment loans, anything above the minimum reduces principal — but confirm with the lender, as some apply extra payments to future instalments instead." },
    ],
    related: ["mortgage-extra-payment-calculator", "compound-interest-visualizer", "fire-calculator-retire-early", "inflation-purchasing-power-calculator"],
  },

  {
    slug: "inflation-purchasing-power-calculator",
    cluster: "finance",
    name: "Money Devaluation Tool",
    h1: "Historical Inflation and Purchasing Power Loss Calculator",
    title: "Inflation Purchasing Power Calculator (Historical)",
    description:
      "See what money from any past year is worth today, how much purchasing power was lost, and the cumulative and annualised inflation rate between two years.",
    keywords: [
      "historical inflation purchasing power loss calculator",
      "what is 1000 dollars in 1990 worth today",
      "cumulative inflation rate between two years",
      "money devaluation calculator usd gbp eur",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: CURRENCIES },
      { id: "amount", label: "Amount", type: "number", def: "1000", min: 1, step: 100 },
      { id: "start", label: "Start year", type: "number", def: "1990", min: 1913, max: 2026 },
      { id: "end", label: "End year", type: "number", def: "2026", min: 1913, max: 2026 },
      { id: "rate", label: "Average annual inflation", type: "number", def: "2.8", unit: "%", min: 0, max: 25, step: 0.1, help: "US long-run CPI average is about 3.1%; recent decade about 2.9%." },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const years = Math.max(0, n(v, "end") - n(v, "start"));
      const r = n(v, "rate") / 100;
      const factor = Math.pow(1 + r, years);
      const amt = n(v, "amount");
      const equivalent = amt * factor;
      const powerLeft = amt / factor;
      const rows: (string | number)[][] = [];
      const step = years > 30 ? 10 : years > 12 ? 5 : 2;
      for (let y = 0; y <= years; y += step) {
        rows.push([n(v, "start") + y, money(amt * Math.pow(1 + r, y), c), money(amt / Math.pow(1 + r, y), c), `${fmt((Math.pow(1 + r, y) - 1) * 100, 1)}%`]);
      }
      return {
        cards: [
          { label: `${money(amt, c)} in ${n(v, "start")} equals`, value: money(equivalent, c), sub: `in ${n(v, "end")} money`, accent: true },
          { label: "Purchasing power left", value: money(powerLeft, c), sub: `${fmt((powerLeft / amt) * 100, 1)}% of the original` },
          { label: "Cumulative inflation", value: `${fmt((factor - 1) * 100, 1)}%`, sub: `over ${years} years` },
          { label: "Value halves every", value: `${fmt(Math.log(2) / Math.log(1 + r), 1)} yrs`, sub: "At this inflation rate" },
        ],
        table: { title: "Year-by-year devaluation path", head: ["Year", "Nominal equivalent", "Real value of the original", "Cumulative inflation"], rows },
        summary: `An annual rate of ${n(v, "rate")}% compounds to ${fmt((factor - 1) * 100, 1)}% over ${years} years — inflation is exponential, not linear.`,
      };
    },
    intro:
      "Inflation compounds the same way investment returns do, which is why a rate that feels harmless in any single year becomes brutal across a career. At 2.8% a year, money loses half its purchasing power in roughly 25 years — the exact span between starting work and mid-career, and the reason cash savings quietly shrink even as the balance grows.",
    sections: [
      { h: "Nominal versus real amounts", body: "A nominal amount is the number printed on the note; a real amount is what it buys. When your salary rises 3% in a 4% inflation year you received a nominal raise and a real pay cut. Every long-horizon financial plan should be stated in real terms so future numbers stay comparable to today's prices." },
      { h: "Why the official basket may not match yours", body: "CPI weights a fixed basket of goods. If your spending skews toward rent, childcare, health insurance or university fees — all of which have run well above headline CPI for decades — your personal inflation rate can be two or three points higher than the published figure." },
      { h: "Assets that historically outran inflation", body: "Broad equities have returned roughly 6-7% above inflation over long horizons, real estate about 1-2% above, gold roughly matches inflation over very long spans with enormous volatility, and cash reliably loses. This is the entire argument for investing rather than saving over multi-decade horizons." },
    ],
    staticTable: {
      title: "What $100 from each year is worth in 2026 dollars",
      head: ["Year", "Equivalent in 2026", "Cumulative inflation", "Purchasing power of $100 then"],
      rows: [
        [1960, "$1,070", "970%", "$9.35"],
        [1970, "$820", "720%", "$12.20"],
        [1980, "$390", "290%", "$25.60"],
        [1990, "$245", "145%", "$40.80"],
        [2000, "$186", "86%", "$53.80"],
        [2010, "$147", "47%", "$68.00"],
        [2020, "$126", "26%", "$79.40"],
      ],
      caption: "Indicative US CPI-based figures, rounded.",
    },
    faqs: [
      { q: "What is $1,000 in 1990 worth today?", a: "Roughly $2,450 in 2026 dollars using US CPI, meaning the original $1,000 retains about 41% of its purchasing power." },
      { q: "How is cumulative inflation calculated?", a: "Compound the annual rate: (1 + r)^years − 1. Twenty-five years at 2.8% gives a cumulative 99.4% rise in prices, so goods cost almost exactly twice as much." },
      { q: "Does a 2% inflation target mean prices are stable?", a: "No. It means prices double roughly every 35 years by design. Central banks target mild inflation because deflation is far more damaging to employment and debt burdens." },
    ],
    related: ["compound-interest-visualizer", "fire-calculator-retire-early", "car-depreciation-calculator", "mortgage-extra-payment-calculator"],
  },

  {
    slug: "side-hustle-profit-calculator",
    cluster: "finance",
    name: "Side Hustle Hourly Gain",
    h1: "Side Hustle Net Profit Calculator (Your Actual Hourly Wage)",
    title: "Side Hustle Net Profit & Real Hourly Wage Calculator",
    description:
      "Strip out materials, platform fees, shipping and tax to find what your side hustle really pays per hour — and what price rise you need to hit a target wage.",
    keywords: [
      "side hustle net profit calculator actual hourly wage",
      "etsy shop profit margin calculator with fees",
      "is my side hustle worth the time calculator",
      "reselling profit per hour after fees",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: CURRENCIES },
      { id: "revenue", label: "Monthly revenue", type: "number", def: "1800", min: 0, step: 50 },
      { id: "materials", label: "Materials / cost of goods", type: "number", def: "540", min: 0, step: 10 },
      { id: "fees", label: "Platform + payment fees", type: "number", def: "11", unit: "%", min: 0, max: 40, step: 0.5 },
      { id: "fixed", label: "Fixed monthly costs", type: "number", def: "60", min: 0, step: 5, help: "Subscriptions, storage, insurance." },
      { id: "hours", label: "Hours worked per month", type: "number", def: "45", min: 1, max: 400 },
      { id: "tax", label: "Tax rate on profit", type: "number", def: "22", unit: "%", min: 0, max: 60 },
      { id: "target", label: "Target hourly wage", type: "number", def: "35", min: 1 },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const rev = n(v, "revenue");
      const feeCost = (rev * n(v, "fees")) / 100;
      const gross = rev - n(v, "materials") - feeCost - n(v, "fixed");
      const net = gross * (1 - n(v, "tax") / 100);
      const hours = Math.max(1, n(v, "hours", 1));
      const perHour = net / hours;
      const targetNet = n(v, "target") * hours;
      const neededRev = (targetNet / (1 - n(v, "tax") / 100) + n(v, "materials") + n(v, "fixed")) / (1 - n(v, "fees") / 100);
      return {
        cards: [
          { label: "Real hourly wage", value: money(perHour, c, 2), sub: `after fees, costs and tax`, accent: true },
          { label: "Net monthly profit", value: money(net, c), sub: `${fmt((net / Math.max(rev, 1)) * 100, 1)}% net margin` },
          { label: "Pre-tax profit", value: money(gross, c), sub: `Fees cost you ${money(feeCost, c)}` },
          { label: "Revenue needed", value: money(neededRev, c), sub: `To earn ${money(n(v, "target"), c)}/hour` },
        ],
        bars: [
          { label: "Materials", value: n(v, "materials"), display: money(n(v, "materials"), c) },
          { label: "Fees", value: feeCost, display: money(feeCost, c) },
          { label: "Fixed", value: n(v, "fixed"), display: money(n(v, "fixed"), c) },
          { label: "Tax", value: Math.max(0, gross - net), display: money(Math.max(0, gross - net), c) },
          { label: "Your profit", value: Math.max(0, net), display: money(net, c) },
        ],
        summary: perHour < n(v, "target")
          ? `You are ${money(n(v, "target") - perHour, c, 2)} an hour short — raise prices by ${fmt(((neededRev - rev) / Math.max(rev, 1)) * 100, 1)}% or cut hours.`
          : `You are clearing your target by ${money(perHour - n(v, "target"), c, 2)} an hour.`,
      };
    },
    intro:
      "Side hustle revenue is a vanity number. The figure that decides whether the venture is worth your evenings is net profit divided by hours actually spent — including sourcing, photography, listing, packing, customer messages and bookkeeping, not just the time making the thing.",
    sections: [
      { h: "Count the invisible hours", body: "Sellers typically underestimate time by 30-50% because they only count production. A handmade item that takes 40 minutes to produce often carries another 25 minutes of listing, photographing, packing and post office time. Track a full week honestly before entering the hours figure above." },
      { h: "Platform fees stack faster than expected", body: "A typical marketplace charges a listing fee, a transaction percentage, a payment processing fee and sometimes an advertising levy. Combined they routinely reach 10-14% of gross revenue. Selling direct removes most of this but transfers customer acquisition cost onto you." },
      { h: "The pricing lever beats the volume lever", body: "Raising prices 10% at constant volume drops straight to profit; selling 10% more units adds materials, fees and hours. If the calculator shows you below target, the required revenue card tells you exactly what turnover — not what volume — clears the bar." },
    ],
    staticTable: {
      title: "Typical fee loads by channel",
      head: ["Channel", "Listing / subscription", "Transaction fee", "Payment fee", "Effective total"],
      rows: [
        ["Etsy", "$0.20 per listing", "6.5%", "3% + $0.25", "~11%"],
        ["eBay", "Free tier", "12.9%", "included", "~13%"],
        ["Amazon Handmade", "None (Handmade)", "15%", "included", "~15%"],
        ["Shopify + Stripe", "$39/month", "0%", "2.9% + $0.30", "~4% + fixed"],
        ["Local markets", "Stall fee", "0%", "1.75% card", "Stall + ~2%"],
      ],
    },
    faqs: [
      { q: "How do I calculate my real hourly wage from a side hustle?", a: "Subtract materials, platform fees, fixed costs and tax from revenue, then divide the remainder by every hour spent on the business, including admin and shipping." },
      { q: "What net margin should a side hustle target?", a: "Physical product businesses typically need 25-40% net margin to be worth the labour; digital products and services can exceed 70% because materials cost effectively nothing." },
      { q: "When should I quit a side hustle?", a: "When the real hourly wage sits persistently below what you could earn in overtime or a second shift, and the gap cannot be closed by raising prices or removing a fee-heavy channel." },
    ],
    related: ["freelance-hourly-rate-calculator", "hourly-to-annual-salary-converter", "youtube-ad-revenue-calculator", "twitch-payout-estimator"],
  },

  {
    slug: "mortgage-extra-payment-calculator",
    cluster: "finance",
    name: "Mortgage Extra Payment",
    h1: "Mortgage Extra Payment Calculator (Interest Saved and Years Cut)",
    title: "Mortgage Extra Payment Calculator — Interest Saved",
    description:
      "See how much interest an extra monthly mortgage payment saves and how many years it removes from the term, with a side-by-side amortisation comparison.",
    keywords: [
      "mortgage extra payment interest savings calculator",
      "how much does 200 extra a month save on mortgage",
      "pay off mortgage early calculator years saved",
      "biweekly vs extra monthly mortgage payment",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: CURRENCIES },
      { id: "amount", label: "Loan amount", type: "number", def: "350000", min: 1000, step: 5000 },
      { id: "rate", label: "Interest rate", type: "number", def: "6.2", unit: "%", min: 0.1, max: 20, step: 0.05 },
      { id: "term", label: "Loan term", type: "number", def: "30", unit: "years", min: 1, max: 40 },
      { id: "extra", label: "Extra monthly payment", type: "number", def: "250", min: 0, step: 25 },
      { id: "lump", label: "One-off lump sum today", type: "number", def: "0", min: 0, step: 1000 },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const P = n(v, "amount"), r = n(v, "rate") / 100 / 12, N = n(v, "term", 30) * 12;
      const base = r === 0 ? P / N : (P * r * Math.pow(1 + r, N)) / (Math.pow(1 + r, N) - 1);
      const sim = (extra: number, lump: number) => {
        let bal = Math.max(0, P - lump), months = 0, interest = 0;
        while (bal > 0.01 && months < 720) {
          const i = bal * r;
          interest += i;
          bal = bal + i - Math.min(bal + i, base + extra);
          months++;
        }
        return { months, interest };
      };
      const a = sim(0, 0), b = sim(n(v, "extra"), n(v, "lump"));
      return {
        cards: [
          { label: "Monthly payment", value: money(base + n(v, "extra"), c), sub: `Base ${money(base, c)} + extra ${money(n(v, "extra"), c)}`, accent: true },
          { label: "Interest saved", value: money(Math.max(0, a.interest - b.interest), c), sub: "Over the life of the loan" },
          { label: "Time saved", value: `${fmt((a.months - b.months) / 12, 1)} yrs`, sub: `Paid off in ${fmt(b.months / 12, 1)} years` },
          { label: "Total interest", value: money(b.interest, c), sub: `Was ${money(a.interest, c)} with no extra` },
        ],
        table: {
          title: "Extra payment sensitivity",
          head: ["Extra per month", "Payoff time", "Total interest", "Interest saved"],
          rows: [0, 100, 200, 300, 500, 1000].map((x) => {
            const r2 = sim(x, n(v, "lump"));
            return [money(x, c), `${fmt(r2.months / 12, 1)} yrs`, money(r2.interest, c), money(Math.max(0, a.interest - r2.interest), c)];
          }),
        },
        summary: `Every extra ${money(100, c)} a month here removes roughly ${fmt((a.months - sim(100, 0).months) / 12, 1)} years from the term.`,
      };
    },
    intro:
      "Early mortgage payments are almost entirely interest. On a 30-year loan at 6.2%, the first year's payments retire under 1.3% of the principal — which is exactly why an extra payment made in year two is worth several times the same payment made in year twenty. Overpayment reduces the balance that all future interest is calculated on.",
    sections: [
      { h: "Where amortisation hides your money", body: "The monthly payment is fixed, but its split shifts. At 6.2% on a 30-year term, the crossover where more of each payment goes to principal than interest arrives around year 18. Any extra payment jumps the queue by reducing principal directly, which compounds forward for the whole remaining term." },
      { h: "Bi-weekly payments versus one extra payment", body: "Paying half the monthly amount every two weeks produces 26 half-payments — thirteen monthly payments a year instead of twelve. That is functionally an extra 8.3% payment, and it typically removes four to six years from a 30-year term. Making one deliberate extra payment a year achieves the same result with less admin." },
      { h: "Overpay or invest?", body: "Overpaying earns a guaranteed, tax-free return equal to your mortgage rate. Above roughly 6% that is competitive with expected equity returns after tax, and it is risk-free. Below 4% the arithmetic usually favours investing — but check for early repayment charges and keep an emergency fund either way." },
    ],
    staticTable: {
      title: "Interest saved on a $350,000 loan at 6.2% over 30 years",
      head: ["Extra monthly", "New term", "Total interest", "Interest saved"],
      rows: [
        ["$0", "30 yrs", "$421,600", "—"],
        ["$100", "27.0 yrs", "$369,300", "$52,300"],
        ["$250", "23.3 yrs", "$309,700", "$111,900"],
        ["$500", "19.1 yrs", "$245,100", "$176,500"],
        ["$1,000", "14.2 yrs", "$176,300", "$245,300"],
      ],
    },
    faqs: [
      { q: "How much does an extra $200 a month save on a mortgage?", a: "On a $350,000 loan at 6.2%, roughly $95,000 of interest and about five years off the term — because the overpayment reduces the balance every future interest charge is based on." },
      { q: "Is it better to overpay the mortgage or invest?", a: "Overpaying gives a guaranteed return equal to your interest rate. Above about 6% that generally beats risk-adjusted investing; below 4% investing usually wins over long horizons." },
      { q: "Do lenders charge for overpayments?", a: "Many fixed-rate mortgages allow 10% of the balance to be overpaid each year penalty-free, with early repayment charges above that. Check your specific terms before committing." },
    ],
    related: ["compound-interest-visualizer", "debt-snowball-vs-avalanche-calculator", "fire-calculator-retire-early", "rent-splitter-by-square-feet"],
  },
];